import { useState, useEffect, useCallback } from 'react'
import {
  getProducts,
  getProductById,
  FirestoreProduct,
  PaginatedResult,
} from '../firebase/firestore'
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore'
// Hook for fetching products with pagination
export function useProducts(
  limitPerPage: number = 12,
  initialFilters: Record<string, any> = {},
  currentUserId?: string,
) {
  const [products, setProducts] = useState<FirestoreProduct[]>([])
  const [lastDoc, setLastDoc] =
    useState<QueryDocumentSnapshot<DocumentData> | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [filters, setFilters] = useState(initialFilters)
  // Initial fetch
  useEffect(() => {
    let isMounted = true
    setInitialLoading(true)
    setError(null)
    const fetchInitialProducts = async () => {
      try {
        const result = await getProducts(
          limitPerPage,
          null,
          filters,
          currentUserId,
        )
        if (isMounted) {
          setProducts(result.items)
          setLastDoc(result.lastDoc)
          setHasMore(result.hasMore)
          setError(null)
        }
      } catch (err) {
        if (isMounted) {
          setError(
            err instanceof Error ? err : new Error('An unknown error occurred'),
          )
          setProducts([])
        }
      } finally {
        if (isMounted) {
          setInitialLoading(false)
        }
      }
    }
    fetchInitialProducts()
    return () => {
      isMounted = false
    }
  }, [limitPerPage, JSON.stringify(filters), currentUserId])
  // Function to load more products
  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return
    setLoading(true)
    setError(null)
    try {
      const result = await getProducts(
        limitPerPage,
        lastDoc,
        filters,
        currentUserId,
      )
      setProducts((prev) => [...prev, ...result.items])
      setLastDoc(result.lastDoc)
      setHasMore(result.hasMore)
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error('An unknown error occurred'),
      )
    } finally {
      setLoading(false)
    }
  }, [hasMore, loading, lastDoc, limitPerPage, filters, currentUserId])
  // Function to update filters
  const updateFilters = useCallback((newFilters: Record<string, any>) => {
    setFilters(newFilters)
    // Reset pagination when filters change
    setLastDoc(null)
    setHasMore(true)
  }, [])
  return {
    products,
    loading,
    initialLoading,
    error,
    hasMore,
    loadMore,
    updateFilters,
    filters,
  }
}
// Hook for fetching a single product
export function useProduct(productId: string | undefined) {
  const [product, setProduct] = useState<FirestoreProduct | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  useEffect(() => {
    let isMounted = true
    setLoading(true)
    setError(null)
    const fetchProduct = async () => {
      if (!productId) {
        setLoading(false)
        setError(new Error('Product ID is required'))
        return
      }
      try {
        const result = await getProductById(productId)
        if (isMounted) {
          setProduct(result)
          setError(null)
        }
      } catch (err) {
        if (isMounted) {
          setError(
            err instanceof Error ? err : new Error('An unknown error occurred'),
          )
          setProduct(null)
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }
    fetchProduct()
    return () => {
      isMounted = false
    }
  }, [productId])
  return { product, loading, error }
}
